import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { costFormatter } from "@phoenix/utils/numberFormatUtils";
import { ChartTypeRenderer, type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import type { TopAgentsByCostQuery } from "./__generated__/TopAgentsByCostQuery.graphql";

const FALLBACK_LABEL = "(unknown)";
const MAX_ITEMS = 10;
const COLOR_PALETTE = ["#06b6d4", "#0891b2", "#0e7490", "#22d3ee", "#67e8f9", "#a5f3fc", "#0284c7", "#0369a1", "#38bdf8", "#7dd3fc"];

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

export function TopAgentsByCost({ projectId, timeRange, chartType = "vertical_bar", widgetId, widgetTitle, onDataExport }: Props) {
  const data = useLazyLoadQuery<TopAgentsByCostQuery>(
    graphql`
      query TopAgentsByCostQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, rootSpansOnly: true, first: 500) {
              edges {
                span: node {
                  agentName
                  trace { costSummary { total { cost } } }
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
    }
  );

  const chartData = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      const key = span.agentName ?? FALLBACK_LABEL;
      map[key] = (map[key] ?? 0) + (span.trace?.costSummary?.total?.cost ?? 0);
    }
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);



  return (
    <ChartTypeRenderer
      data={chartData}
      chartType={chartType}
      valueFormatter={costFormatter}
      colorPalette={COLOR_PALETTE}
      emptyIcon="🤖"
      emptyText="No agent cost data"
    />
  );
}
